'use client';

/**
 * FormCoverPage — Zoho-style welcome/cover screen displayed before a form.
 *
 * Configured via `form_schema.cover_page`:
 *   {
 *     enabled: boolean;
 *     title?: string;
 *     description?: string;
 *     image_url?: string;
 *     estimated_time?: string;      // e.g. "5 minutes"
 *     start_button_label?: string;  // default: "Start"
 *   }
 *
 * Phase 5, task 5.3 of the Total Forms Renovation Plan.
 */

import React from 'react';
import { Clock, ArrowRight } from 'lucide-react';
import { type FormThemeConfig, resolveFormTheme } from './formThemes';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface CoverPageConfig {
  enabled: boolean;
  title?: string;
  description?: string;
  image_url?: string;
  estimated_time?: string;
  start_button_label?: string;
}

export interface FormCoverPageProps {
  config: CoverPageConfig;
  /** Fallback title (form title) when cover_page.title is not set */
  formTitle?: string;
  formTheme?: Partial<FormThemeConfig> | null;
  onStart: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function FormCoverPage({ config, formTitle, formTheme, onStart }: FormCoverPageProps) {
  const ft = resolveFormTheme(formTheme);
  const title = config.title || formTitle || 'Welcome';
  const btnLabel = config.start_button_label || 'Start';

  return (
    <div
      className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-16 text-center"
      style={{
        background: ft.bg_color,
        fontFamily: ft.font_family,
      }}
    >
      {/* Cover image */}
      {config.image_url && (
        <div className="mb-8 w-full max-w-md">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={config.image_url}
            alt=""
            className="w-full rounded-2xl shadow-lg object-cover max-h-56"
            style={{ borderRadius: ft.border_radius ? `${ft.border_radius}px` : undefined }}
          />
        </div>
      )}

      {/* Title */}
      <h1
        className="text-3xl md:text-4xl font-extrabold leading-tight max-w-lg"
        style={{ color: ft.question_color }}
      >
        {title}
      </h1>

      {/* Description */}
      {config.description && (
        <p
          className="mt-4 text-base md:text-lg leading-relaxed max-w-md opacity-80"
          style={{ color: ft.answer_color }}
        >
          {config.description}
        </p>
      )}

      {/* Estimated time */}
      {config.estimated_time && (
        <div
          className="mt-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
          style={{
            background: `${ft.accent_color}15`,
            color: ft.accent_color,
          }}
        >
          <Clock className="w-3.5 h-3.5" />
          Estimated time: {config.estimated_time}
        </div>
      )}

      {/* Start button */}
      <button
        onClick={onStart}
        className="mt-10 inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-base font-bold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
        style={{
          background: ft.header_gradient || ft.accent_color,
          borderRadius: ft.border_radius ? `${ft.border_radius}px` : undefined,
        }}
      >
        {btnLabel}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
